import { CurecallList, CurecallListHeaders, ECurecallDropdownPosition } from '@honestica/curecall-ui'
import { useSession } from 'src/providers/auth.provider'
import { useTranslation } from 'react-i18next'
import { getPhone } from 'src/utils/phone.util'
import { ReactNode, useState } from 'react'
import {
  ActivateCell,
  UserEmailCell,
  TitleIconCell,
  IdentityCell,
  BoldTypoCell,
  ActionsCell,
  ResetTwoFactorConfirmation,
} from './users.cells'
import { TUser, TUserProject, EUserTitle, USER_LOCALES } from 'src/types/user'
import {
  TPermission,
  EPermissionGrantSubjects,
  EPermissionGrantActions,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'
import { useQuery } from '@tanstack/react-query'
import { UserProjectService } from 'src/services/user-project.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { TMedicalStaffMember } from 'src/types/medical-staff-member'
import { TNavigationLocales } from 'src/types/locales'

export const LIST_HEADERS = [
  // Change the order will affect the list headers order
  'activate',
  'title',
  'user',
  'email',
  'phone',
  'locale',
  'permission',
  'actions',
]

// TODO : replace me by TUserProject
export type TUserProjectForm = {
  UserId?: number
  isActive?: boolean
  PermissionId?: number
  User?: {
    title?: EUserTitle | null
    firstname?: string | null
    lastname?: string | null
    email?: string | null
    phone?: string | null
    locale?: TNavigationLocales | null
    // Create or update 'admin' is prohibited
  }
  medicalStaffMember?: TMedicalStaffMember
}

// TODO : refacto this modalProps to be more simplier
export type TModalProps =
  | TPostModalProps
  | TPutModalProps
  | TDeleteModalProps
  | TSummaryModalProps
  | undefined

export type TPostModalProps = {
  mode: 'POST'
  UserProject: undefined
}
export type TPutModalProps = {
  mode: 'PUT'
  UserProject: TUserProjectForm
}
type TDeleteModalProps = {
  mode: 'DELETE'
  UserProject: TUserProject
}
type TSummaryModalProps = {
  mode: 'SUMMARY'
  UserProject: TUserProject
  password: string
}

interface UserProjectListTableProps {
  setModalProps: (modalProps: TModalProps) => void
  orderBy: { key: (typeof LIST_HEADERS)[number]; descOrder: boolean }
  setOrderBy: (orderBy: { key: (typeof LIST_HEADERS)[number]; descOrder: boolean }) => void
  projectPermissionList: TPermission[]
}
export default function UserProjectListTable({
  setModalProps,
  orderBy,
  setOrderBy,
  projectPermissionList,
}: UserProjectListTableProps) {
  const { currentUser } = useSession()
  const { project } = useCurrentProject()
  const { t } = useTranslation()

  const [resetTwoFactorUser, setResetTwoFactorUser] = useState<{
    userId: number
    userName: string
  } | null>(null)

  const {
    isLoading: isUserProjectListLoading,
    error: getUserProjectListError,
    data: userProjectList,
  } = useQuery({
    queryKey: ['user-project-list'],
    queryFn: () => UserProjectService.getUserProjectList(project.id),
  })

  const getUserProjectPermission = (UserProjectPermissionId: number) => {
    return projectPermissionList?.find(
      (projectPermission) => projectPermission.id === UserProjectPermissionId,
    )
  }

  const isAdminPermission = (permission: TPermission) =>
    permission.grants.some(
      (grant) =>
        grant.subject === EPermissionGrantSubjects.ALL && grant.actions.includes(EPermissionGrantActions.ALL),
    )

  const getUserProjectLocale = (locale: string) => {
    return USER_LOCALES?.find((item) => {
      return item.code === locale
    })?.locale
  }

  const tableRowsData: Array<Record<(typeof LIST_HEADERS)[number], object | ReactNode>> =
    userProjectList?.map((UserProject, index) => ({
      activate: UserProject.User?.email !== currentUser?.email && (
        <ActivateCell
          userProject={UserProject}
          setModalProps={setModalProps}
          tooltipPosition={index < userProjectList?.length - 1 ? 'bottom-start' : 'top-start'}
        />
      ),
      title: TitleIconCell({ title: UserProject.User?.title }),
      user: {
        observedData: UserProject.User?.lastname,
        component: IdentityCell({ ...(UserProject.User as TUser) }),
      },
      email: {
        component: UserEmailCell({
          userEmail: UserProject.User?.email as string,
          isEmailVerified: !!UserProject.UserAuth?.isEmailVerified,
          tooltipPosition: index < userProjectList?.length - 1 ? 'bottom-start' : 'top-start',
          tooltipLabel: t('pages.users-page.tooltip-user-email-not-verified'),
        }),
        observedData: UserProject.User?.email,
      },
      phone: <div className="c-min-w-fit c-w-max">{getPhone(t, UserProject.User?.phone)}</div>,
      locale: {
        component:
          UserProject.User?.locale &&
          BoldTypoCell(t(`pages.users-page.${getUserProjectLocale(UserProject.User?.locale)}`)),
      },
      permission: {
        component: (() => {
          const permission = UserProject.PermissionId
            ? getUserProjectPermission(UserProject.PermissionId)
            : undefined
          if (!permission) return undefined
          return (
            <span className="c-flex c-items-center c-gap-1">
              <span className="c-font-semibold">{permission.name}</span>
              {isAdminPermission(permission) && <AdminOnlyBadge />}
            </span>
          )
        })(),
        observedData: UserProject.User?.email === currentUser?.email,
      },
      actions:
        UserProject.User?.email !== currentUser?.email &&
        ActionsCell({
          updateLabel: t('pages.users-page.edit'),
          deleteLabel: t('pages.users-page.delete'),
          dropdownButtonLabel: t('pages.projects-page.actions'),
          menuPosition:
            userProjectList?.length > 3 && index === userProjectList?.length - 1
              ? ECurecallDropdownPosition['TOP_LEFT']
              : ECurecallDropdownPosition['BOTTOM_LEFT'],
          onDeleteUserClick: () => setModalProps({ mode: 'DELETE', UserProject }),
          onUpdateUserClick: () => setModalProps({ mode: 'PUT', UserProject }),
          ...(currentUser?.isAdmin && UserProject.UserId
            ? {
                resetTwoFactorLabel: t('pages.users-page.reset-two-factor'),
                onResetTwoFactorClick: () =>
                  setResetTwoFactorUser({
                    userId: UserProject.UserId,
                    userName:
                      `${UserProject.User?.firstname ?? ''} ${UserProject.User?.lastname ?? ''}`.trim(),
                  }),
              }
            : {}),
        }),
    })) ?? []

  const SORTABLE_HEADERS: Array<(typeof LIST_HEADERS)[number]> = ['user']

  const tableColumnsData: CurecallListHeaders<Record<(typeof LIST_HEADERS)[number], ReactNode>> =
    LIST_HEADERS.map((columnKey) => ({
      onHeaderClick: SORTABLE_HEADERS.includes(columnKey)
        ? () => setOrderBy({ key: columnKey, descOrder: !orderBy.descOrder })
        : undefined,
      cellClassName: 'tw-align-middle',
      headerClassName: ['activate', 'title'].includes(columnKey)
        ? 'c-w-12'
        : columnKey === 'actions'
          ? '[&>*]:c-ml-5 c-w-fit'
          : undefined,
      label: t(`pages.users-page.${columnKey}`),
      key: columnKey,
    }))

  if (getUserProjectListError) {
    return <ErrorQueryHandler error={getUserProjectListError} />
  }

  return (
    <>
      <CurecallList
        isLoading={isUserProjectListLoading}
        loadingLabel={t('pages.users-page.table-loading')}
        noDataLabel={t('pages.users-page.table-no-data')}
        rowsData={tableRowsData}
        headersData={tableColumnsData}
        currentOrder={orderBy}
        isFirstRows={{ matchingHeader: 'permission', matchingValue: true }}
        isDisabledRows={{ matchingHeader: 'activate', matchingValue: false }}
        isSelectedRows={{ matchingHeader: 'email', matchingValue: currentUser?.email }}
      />
      {resetTwoFactorUser && (
        <ResetTwoFactorConfirmation
          isOpen={!!resetTwoFactorUser}
          userId={resetTwoFactorUser.userId}
          userName={resetTwoFactorUser.userName}
          onClose={() => setResetTwoFactorUser(null)}
        />
      )}
    </>
  )
}
